import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Database } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const MigrateData = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState<string>("");

  if (!isAdmin) {
    navigate("/");
    return null;
  }

  const runCleanup = async () => {
    setDeleting(true);
    setProgress("Starting cleanup...");
    
    try {
      let totalDeleted = 0;
      let hasMore = true;
      
      while (hasMore) {
        setProgress(`Deleting batch... (${totalDeleted} deleted)`);
        
        const { data, error } = await supabase.functions.invoke('migrate-generations');
        
        if (error) throw error;
        
        totalDeleted += data.deleted || 0;
        hasMore = data.remaining === 'More records to process - run again';
        
        setProgress(`Deleted ${totalDeleted} old generations.`);
        
        if (hasMore) {
          // Wait before next batch
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      toast({
        title: "Cleanup Complete!",
        description: `Successfully deleted ${totalDeleted} old generations with large base64 images. Database size will be significantly reduced.`,
      });
      
      setProgress(`✅ All done! Deleted ${totalDeleted} old generations.`);
    } catch (error: any) {
      console.error('Cleanup error:', error);
      
      const errorMessage = error?.message || error?.error || "Failed to delete data";
      const errorDetails = error?.details ? JSON.stringify(error.details, null, 2) : "";
      
      toast({
        title: "Cleanup Error",
        description: errorDetails ? `${errorMessage}\n\nDetails: ${errorDetails}` : errorMessage,
        variant: "destructive",
      });
      
      setProgress(`❌ Error: ${errorMessage}${errorDetails ? '\n' + errorDetails : ''}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/admin")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Admin
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-6 w-6" />
              Clear All Generations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                This will delete ALL user generations from the database. Hairstyle reference images will be kept.
              </p>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                <li>Deletes 10 generations per batch</li>
                <li>Removes all user generation history</li>
                <li>Frees up database space immediately</li>
                <li>Cannot be undone - keeps clicking until all are deleted</li>
              </ul>
              <p className="text-sm font-semibold mt-4 text-destructive">
                ⚠️ Warning: This will permanently delete ALL generation data
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Hairstyle reference images in the admin panel will NOT be deleted
              </p>
            </div>

            {progress && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm font-mono">{progress}</p>
              </div>
            )}

            <Button
              onClick={runCleanup}
              disabled={deleting}
              variant="destructive"
              className="w-full"
              size="lg"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Database className="mr-2 h-4 w-4" />
                  Clear All Generations
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MigrateData;
